import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Home from "../pages/Home";
import Dashboard from "../pages/Dashboard";
import Settings from "../pages/Settings";
import Resources from "../pages/Resources";
import ContactUs from "../pages/ContactUs";
import GettingStarted from "../pages/help/GettingStarted";
import UserGuides from "../pages/help/UserGuides";
import FAQs from "../pages/help/FAQs";
import PlansEnhanced from "../pages/PlansEnhanced";
import OrganizationSignup from "../pages/OrganizationSignup";
import IndividualToOrgUpgrade from "../pages/IndividualToOrgUpgrade";
import SubscriptionDashboard from "./subscription/SubscriptionDashboard";
import OrganizationSettings from "../pages/organization/OrganizationSettings";
import TeamManagement from "../pages/organization/TeamManagement";
import RolesPermissions from "../pages/organization/RolesPermissions";
import OrganizationActivities from "../pages/organization/OrganizationActivities";
import DroneInventory from "../pages/fleet/DroneInventory";
import BatteryInventory from "../pages/fleet/BatteryInventory";
import PayloadInventory from "../pages/fleet/PayloadInventory";
import MaintenanceSchedule from "../pages/fleet/MaintenanceSchedule";
import ComplianceDashboard from "../pages/compliance/ComplianceDashboard";
import PilotLicenses from "../pages/compliance/PilotLicenses";
import Checklists from "../pages/compliance/Checklists";
import Incidents from "../pages/compliance/Incidents";
import FlightAnalytics from "../pages/analytics/FlightAnalytics";
import UtilizationReports from "../pages/analytics/UtilizationReports";
import CostAnalysis from "../pages/analytics/CostAnalysis";
import FlightLogs from "../pages/analytics/FlightLogs";
import InvitationAccept from "../pages/InvitationAccept";
import RegistrationForm from "../pages/auth/RegistrationForm";
import ProfilePage from "../pages/auth/ProfilePage";
import ConfirmEmail from "../pages/email/confirmEmail";
import EmailAlert from "../pages/email/emailAlert";
import NavPageStyles from "../styles/NavPage.module.css"; // Import CSS file
import PrivacyPolicy from "../pages/privacy-policy";
import LaancGeneral from "../pages/laancGeneral";
import TermConditions from "../pages/termsConditions";
import RequestForgotPassword from "../pages/auth/RequestForgotPassword";
import UpdatePassword from "../pages/auth/UpdatePassword";
import ErrorPage from "../pages/CommonError";
import MissionLog from "../pages/missionLogs/MissionLog";
import MissionCalendarPage from "../pages/missionLogs/MissionCalendarPage";
import MissionTemplates from "../pages/missionLogs/MissionTemplates";
import LaancStatement from "../pages/LaancStatement";
import DownloadOTP from "../pages/FileDownload";
import UnsubscribePage from "../pages/missionLogs/fileSection/unsubscribeEmail";
import ErrorNotification from "./ErrorNotification";
import ProtectedRoute from "../pages/auth/ProtectedRoute";
import PermissionProtectedRoute from "../pages/auth/PermissionProtectedRoute";
import RegisterRoute from "../pages/auth/RegisterRoute";
import SubscriptionGuard from "../pages/auth/SubscriptionGuard";


function NavPage({ selectedPurpose, parentSelectedLayers, sendParentMapLoadLayers }) {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [showError, setShowError] = useState(false);
    const location = useLocation(); // Get the current route

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    };

    useEffect(() => {
        // console.log(selectedPurpose);
    }, [selectedPurpose]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (windowSize.width < 600 || windowSize.height < 400) {
            setShowError(true);
        } else {
            setShowError(false);
        }
    }, [windowSize]);

    const onMapLoadLayers = (layers) => {
        //console.log("NavPage: received data from Home:" + layers);
        sendParentMapLoadLayers(layers);
    };

    return (
        <div className={`${NavPageStyles.nav_page_container} bg-gradient-to-br from-light-gray-50 via-white to-white min-h-screen`}>
            <ErrorNotification showError={showError} location={location} />
            <Routes>
                <Route
                    path="/"
                    key={selectedPurpose}
                    element={
                        <Home
                            selectedLayers={parentSelectedLayers}
                            sendDataToParent={onMapLoadLayers}
                            selectedPurpose={selectedPurpose}
                        />
                    }
                />
                <Route path="/home" element={<Home />} />
                <Route path="/operations/create" element={
                    <PermissionProtectedRoute requiredPermission="CREATE_OPERATIONS" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <Home />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <PermissionProtectedRoute requiredPermission="VIEW_DASHBOARD" redirectTo="/operations/create">
                            <SubscriptionGuard>
                                <Dashboard />
                            </SubscriptionGuard>
                        </PermissionProtectedRoute>
                    </ProtectedRoute>
                } />
                <Route path="/settings/organization" element={
                    <PermissionProtectedRoute requiredPermission="MANAGE_ORGANIZATION" redirectTo="/dashboard">
                        <OrganizationSettings />
                    </PermissionProtectedRoute>
                } />
                <Route path="/settings/team" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <TeamManagement />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/settings/roles" element={
                    <PermissionProtectedRoute requiredPermission="MANAGE_ROLES" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <RolesPermissions />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/settings/billing" element={
                    <Navigate to="/settings/organization?tab=billing" replace />
                } />
                <Route path="/organization/:orgId/activities" element={
                    <ProtectedRoute>
                        <PermissionProtectedRoute requiredPermission="VIEW_DASHBOARD" redirectTo="/operations/create">
                            <SubscriptionGuard>
                                <OrganizationActivities />
                            </SubscriptionGuard>
                        </PermissionProtectedRoute>
                    </ProtectedRoute>
                } />
                <Route path="/fleet/drones" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <DroneInventory />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/fleet/batteries" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <BatteryInventory />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/fleet/payloads" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <PayloadInventory />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/fleet/maintenance-schedule" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <MaintenanceSchedule />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/compliance/dashboard" element={
                    <ProtectedRoute>
                        <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                            <SubscriptionGuard>
                                <ComplianceDashboard />
                            </SubscriptionGuard>
                        </PermissionProtectedRoute>
                    </ProtectedRoute>
                } />
                {/* Legacy route redirect for backwards compatibility */}
                <Route path="/analytics/compliance" element={
                    <ProtectedRoute>
                        <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                            <SubscriptionGuard>
                                <ComplianceDashboard />
                            </SubscriptionGuard>
                        </PermissionProtectedRoute>
                    </ProtectedRoute>
                } />
                <Route path="/compliance/licenses" element={
                    <ProtectedRoute>
                        <PermissionProtectedRoute requiredPermission="VIEW_PILOT_LICENSES" redirectTo="/dashboard">
                            <SubscriptionGuard>
                                <PilotLicenses />
                            </SubscriptionGuard>
                        </PermissionProtectedRoute>
                    </ProtectedRoute>
                } />
                <Route path="/compliance/checklists" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <Checklists />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/compliance/incidents" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <Incidents />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/analytics/flights" element={
                    <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <FlightAnalytics />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/analytics/utilization" element={
                    <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <UtilizationReports />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/analytics/costs" element={
                    <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <CostAnalysis />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/analytics/flight-logs" element={
                    <PermissionProtectedRoute requiredPermission="VIEW_ANALYTICS" redirectTo="/dashboard">
                        <SubscriptionGuard>
                            <FlightLogs />
                        </SubscriptionGuard>
                    </PermissionProtectedRoute>
                } />
                <Route path="/Settings" element={<Settings />} />
                <Route path="/Resources" element={<Resources />} />
                <Route path="/ContactUs" element={<ContactUs />} />
                <Route path="/help/getting-started" element={<GettingStarted />} />
                <Route path="/help/guides" element={<UserGuides />} />
                <Route path="/help/faqs" element={<FAQs />} />
                {/* Mission Log — list view */}
                <Route path="/MissionLog" element={
                    <ProtectedRoute>
                        <MissionLog />
                    </ProtectedRoute>
                } />
                <Route path="/missionlog" element={
                    <ProtectedRoute>
                        <MissionLog />
                    </ProtectedRoute>
                } />
                {/* Mission Calendar — dedicated calendar view */}
                <Route path="/mission-calendar" element={
                    <ProtectedRoute>
                        <MissionCalendarPage />
                    </ProtectedRoute>
                } />
                <Route path="/operations/templates" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <MissionTemplates />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/Plans" element={<PlansEnhanced />} />
                <Route path="/subscriptions" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <SubscriptionDashboard />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/signup/organization" element={<OrganizationSignup />} />
                <Route path="/upgrade/organization" element={<IndividualToOrgUpgrade />} />
                <Route path="/invitations/accept/:token" element={<InvitationAccept />} />
                <Route path="/register" element={
                    <RegisterRoute>
                        <RegistrationForm />
                    </RegisterRoute>
                } />
                <Route path="/profile" element={
                    <ProtectedRoute>
                        <SubscriptionGuard>
                            <ProfilePage />
                        </SubscriptionGuard>
                    </ProtectedRoute>
                } />
                <Route path="/verify-email/:id" element={<ConfirmEmail />} />
                <Route path="/confirmation-email" element={<EmailAlert />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/forgot-password" element={<RequestForgotPassword />} />
                <Route path="/reset-password" element={<UpdatePassword />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                    path="/request-otp/:shareId/:userId/:recipientName/:operationId/:fileName/:fileType/:recipientId"
                    element={<DownloadOTP />}
                />
                <Route path="/laanc" element={<LaancGeneral />} />
                <Route path="/laanc-statement" element={<LaancStatement />} />
                <Route path="/terms-conditions" element={<TermConditions />} />
                <Route path="*" element={<ErrorPage />} />

            </Routes>
        </div>
    );
}

export default NavPage;
